import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import { useGlobalConfig } from 'context/GlobalContext';
import { DataScenariosApi, CreateDataScenarioRequest, getTemplateNameForApi } from 'apis/dataScenariosAPI';
import { CMSPilotApis } from 'apis/cmsPilotAPI';
import { toast } from 'utils/toast';

// Locale type from API
interface LocaleOption {
  code: string;
  name: string;
}

const LineArrowIcon = (props: any) => (
  <Box
    {...props}
    sx={{
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...props.sx
    }}
  >
    <Box
      component="svg"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity="0.6"
      />
    </Box>
  </Box>
);


interface Payload {
  id: string;
  endpoint: string;
  response: string;
  expanded: boolean;
  isValidJson: boolean;
}

interface AddDataScenariosProps {
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  pilotId: string;
  templateKey: string;       // e.g., "EMAIL.SF.MONTHLY_SUMMARY"
  templateDisplayName: string; // e.g., "Monthly Summary"
  elementKey?: string;       // e.g., "HEADER_V4" or undefined for full template
  elementDisplayName?: string; // e.g., "Header" or "Full Template"
}

export const AddDataScenarios: React.FC<AddDataScenariosProps> = ({
  open,
  onClose,
  onSave,
  pilotId,
  templateKey,
  templateDisplayName,
  elementKey,
  elementDisplayName = 'Full Template'
}) => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  
  // Form state
  const [scenarioId, setScenarioId] = useState('');
  const [label, setLabel] = useState('');
  const [configLevel] = useState('Project');
  const [templateType, setTemplateType] = useState('Email');
  const [locale, setLocale] = useState('English (US)');
  const [fuelType, setFuelType] = useState('ELECTRIC');
  const [payloads, setPayloads] = useState<Payload[]>([
    {
      id: '1',
      endpoint: '',
      response: '{}',
      expanded: true,
      isValidJson: true
    }
  ]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  const [locales, setLocales] = useState<LocaleOption[]>([]);

  useEffect(() => {
    const fetchLocales = async () => {
      try {
        const response = await CMSPilotApis.getSupportedLocales(rc);
        if (!response.error && response.data) {
          setLocales(response.data);
        }
      } catch (error) {
        console.error('[AddDataScenarios] Failed to fetch locales:', error);
      }
    };
    
    fetchLocales();
  }, [rc]);

  const getLocaleCode = (name: string): string => {
    const found = locales.find(l => l.name === name);
    if (found) return found.code;
    
    const localeMap: Record<string, string> = {
      'English (US)': 'en_US',
      'English (UK)': 'en_GB',
      'Spanish': 'es_ES',
      'Spanish (US)': 'es_US',
      'French': 'fr_FR',
      'French (Canada)': 'fr_CA',
      'German': 'de_DE',
      'Portuguese': 'pt_PT',
      'Portuguese (Brazil)': 'pt_BR',
      'Chinese (Simplified)': 'zh_CN',
      'Chinese (Traditional)': 'zh_TW',
      'Japanese': 'ja_JP',
      'Korean': 'ko_KR',
    };
    
    return localeMap[name] || name;
  };

  const isValidJson = (str: string): boolean => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to format JSON
  const formatJson = (str: string): string => {
    try {
      const parsed = JSON.parse(str);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return str;
    }
  };

  const handleAddPayload = () => {
    const newPayload: Payload = {
      id: Date.now().toString(),
      endpoint: '',
      response: '{}',
      expanded: true,
      isValidJson: true
    };
    setPayloads([...payloads, newPayload]);
  };

  const handleDeletePayload = (id: string) => {
    if (payloads.length > 1) {
      setPayloads(payloads.filter(p => p.id !== id));
    } else {
      toast.warning('At least one payload is required');
    }
  };

  const handleTogglePayload = (id: string) => {
    setPayloads(payloads.map(p => 
      p.id === id ? { ...p, expanded: !p.expanded } : p
    ));
  };

  const handleUpdatePayload = (id: string, field: 'endpoint' | 'response', value: string) => {
    setPayloads(payloads.map(p => 
      p.id === id ? { 
        ...p, 
        [field]: value,
        isValidJson: field === 'response' ? isValidJson(value) : p.isValidJson
      } : p
    ));
  };

  const handleFormatJson = (id: string) => {
    setPayloads(payloads.map(p => {
      if (p.id === id && isValidJson(p.response)) {
        return { ...p, response: formatJson(p.response) };
      }
      return p;
    }));
  };

  // Validate form before submission
  const validateForm = (): string | null => {
    if (!scenarioId.trim()) {
      return 'Scenario ID is required';
    }
    if (!label.trim()) {
      return 'Label is required';
    }
    if (!pilotId) {
      return 'Please select a project first';
    }
    if (!templateKey) {
      return 'Template is required';
    }
    
    // Validate payloads
    for (let i = 0; i < payloads.length; i++) {
      const payload = payloads[i];
      if (!payload.endpoint.trim()) {
        return `Payload ${i + 1}: Endpoint is required`;
      }
      if (!payload.isValidJson) {
        return `Payload ${i + 1}: Response must be valid JSON`;
      }
    }
    
    return null;
  };

  const handleSave = async () => {
    // Validate form
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const templateNameForApi = getTemplateNameForApi(templateKey);
      
      // Determine elementNames based on templateType
      let elementNames: string[];
      if (templateType === 'Web') {
        elementNames = [templateNameForApi];
      } else {
        // Use elementKey if provided, otherwise default to FULL_TEMPLATE
        elementNames = elementKey ? [elementKey] : ['FULL_TEMPLATE'];
      }

      // Transform payloads to API format
      const transformedPayloads = payloads.map(p => ({
        templateTypeName: p.endpoint.trim(),
        payload: JSON.parse(p.response)
      }));
      
      const requestData: CreateDataScenarioRequest = {
        pilotId: pilotId,
        scenarioId: scenarioId.trim(),
        scenarioDisplayName: label.trim(),
        productType: templateType,
        scenarioLocale: getLocaleCode(locale) || 'en_US',
        fuelType: fuelType,
        templateName: templateNameForApi,
        elementNames: elementNames,
        defaultScenario: false,
        userClusters: ['DEFAULT'],
        scenarioDocumentation: `Adding the Data scenarios ${label.trim()} - ${scenarioId.trim()}`,
        payloads: transformedPayloads
      };

      console.log('[AddDataScenarios] Creating scenario with data:', requestData);

      // Call the API
      const response = await DataScenariosApi.createDataScenarioWithPayloads(rc, requestData);

      if (response.success) {
        toast.success(`Scenario "${label}" created successfully`);
        onSave?.();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to create scenario');
      }
    } catch (error: any) {
      console.error('[AddDataScenarios] Error creating scenario:', error);
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing while saving
    
    // Reset form
    setScenarioId('');
    setLabel('');
    setTemplateType('Email');
    setLocale('English (US)');
    setFuelType('ELECTRIC');
    setPayloads([
      {
        id: '1',
        endpoint: '',
        response: '{}',
        expanded: true,
        isValidJson: true
      }
    ]);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 3,
          maxWidth: '800px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        pb: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Add New Project Scenario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define the name and JSON data for this scenario's API mocks.
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Scenario ID and Label Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Scenario ID <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g., my-unique-id"
                value={scenarioId}
                onChange={(e) => setScenarioId(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Label <span style={{ color: theme.palette.error.main }}>*</span>
              </Typography>
              <TextField
                placeholder="e.g., High Usage, Logged In"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                fullWidth
                size="small"
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper
                  }
                }}
              />
            </Box>
          </Box>

          {/* Config Level, Template Type, Locale Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Config Level
              </Typography>
              <TextField
                value={configLevel}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  <MenuItem value="Email">Email</MenuItem>
                  <MenuItem value="SMS">SMS</MenuItem>
                  <MenuItem value="HER">HER</MenuItem>
                  <MenuItem value="Web">Web</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Locale
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  {locales.length > 0 ? (
                    locales.map((loc) => (
                      <MenuItem key={loc.code} value={loc.name}>
                        {loc.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={locale}>{locale}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Template Name, Element Name, Fuel Type Row */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Name
              </Typography>
              <TextField
                value={templateDisplayName}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Element Name
              </Typography>
              <TextField
                value={elementDisplayName}
                disabled
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                  }
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Fuel Type
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={fuelType}
                  onChange={(e) => setFuelType(e.target.value)}
                  IconComponent={LineArrowIcon}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    '& .MuiSelect-icon': {
                      marginRight: 0.5,
                      color: theme.palette.text.secondary
                    }
                  }}
                >
                  <MenuItem value="ELECTRIC">ELECTRIC</MenuItem>
                  <MenuItem value="GAS">GAS</MenuItem>
                  <MenuItem value="DUAL">DUAL</MenuItem>
                  <MenuItem value="WATER">WATER</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Scenario Payloads Section */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}>
              Scenario Payloads
            </Typography>

            {payloads.map((payload, index) => (
              <Accordion
                key={payload.id}
                expanded={payload.expanded}
                onChange={() => handleTogglePayload(payload.id)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  '&.Mui-expanded': { margin: '0 0 8px 0' }
                }}
              >
                <AccordionSummary
                  expandIcon={
                    payload.expanded ? (
                      <ExpandLessIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    ) : (
                      <ExpandMoreIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                    )
                  }
                  sx={{
                    minHeight: 36,
                    '&.Mui-expanded': { minHeight: 36 },
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      my: 0.5
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Payload {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePayload(payload.id);
                      }}
                      disabled={isLoading}
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.error.main, 0.1)
                        }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        Endpoint <span style={{ color: theme.palette.error.main }}>*</span>
                      </Typography>
                      <TextField
                        value={payload.endpoint}
                        onChange={(e) => handleUpdatePayload(payload.id, 'endpoint', e.target.value)}
                        placeholder="e.g., bill-comparison, itemization-widget-data"
                        fullWidth
                        size="small"
                        disabled={isLoading}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper
                          }
                        }}
                      />
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Response (JSON) <span style={{ color: theme.palette.error.main }}>*</span>
                          </Typography>
                          {/* JSON validation indicator */}
                          {payload.response && (
                            <Tooltip title={payload.isValidJson ? 'Valid JSON' : 'Invalid JSON'}>
                              {payload.isValidJson ? (
                                <CheckCircleOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.success.main }} 
                                />
                              ) : (
                                <ErrorOutlineIcon 
                                  sx={{ fontSize: 16, color: theme.palette.error.main }} 
                                />
                              )}
                            </Tooltip>
                          )}
                        </Box>
                        {/* Format JSON button */}
                        <Tooltip title="Format JSON">
                          <IconButton
                            size="small"
                            onClick={() => handleFormatJson(payload.id)}
                            disabled={!payload.isValidJson || isLoading}
                            sx={{ 
                              color: payload.isValidJson ? theme.palette.primary.main : theme.palette.text.disabled,
                              p: 0.5
                            }}
                          >
                            <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <TextField
                        value={payload.response}
                        onChange={(e) => handleUpdatePayload(payload.id, 'response', e.target.value)}
                        multiline
                        rows={4}
                        fullWidth
                        size="small"
                        disabled={isLoading}
                        placeholder='{"key": "value"}'
                        error={payload.response !== '' && !payload.isValidJson}
                        helperText={payload.response !== '' && !payload.isValidJson ? 'Invalid JSON format' : ''}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            '& textarea': {
                              resize: 'vertical'
                            }
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPayload}
              disabled={isLoading}
              sx={{ 
                mt: 1,
                textTransform: 'none'
              }}
            >
              Add Payload
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          sx={{ 
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || payloads.some(p => !p.isValidJson)}
          sx={{ 
            textTransform: 'none',
            minWidth: 120
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Save Scenario'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
